import { useRef, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, rawFetch } from "@/lib/api"
import { getTheme, setTheme, type Theme } from "@/lib/theme"
import { auth } from "@/lib/auth"
import { Badge, Button, Card, CardBody, CardHeader, Input, Sheet, Spinner, Toast } from "@/components/ui"
import { BuildCard } from "@/components/BuildStamp"
import type { MySecretEntry } from "@/lib/types"
import { MODEL_OPTIONS, getStoredModel, setStoredModel } from "@/lib/models"
import { setLocale, useT, type Locale } from "@/lib/i18n"
import { currentSubscription, pushSupported, subscribePush, unsubscribePush } from "@/lib/push"
import { useEffect } from "react"
import { KeyRoundIcon, PauseIcon, PlayIcon, EyeOffIcon } from "lucide-react"

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
})

interface PauseOut { paused: boolean }

function SettingsPage() {
  const me = auth.me()
  const qc = useQueryClient()
  // Global pause switch — lives under /settings/pause (not /pause flat).
  // It's a system-wide kill switch, not per-user; per-user pause is on
  // the User row and surfaces in the sidebar badge.
  const pause = useQuery({
    queryKey: ["my-pause"],
    queryFn: () => api<PauseOut>("/api/dashboard/settings/pause"),
  })
  const togglePause = useMutation({
    mutationFn: (paused: boolean) =>
      api("/api/dashboard/settings/pause", { method: "PUT", body: { paused, reason: "" } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-pause"] })
      auth.refreshMe()
    },
  })

  const secrets = useQuery({
    queryKey: ["my-secrets"],
    queryFn: () => api<MySecretEntry[]>("/api/me/secrets"),
  })

  const [editing, setEditing] = useState<MySecretEntry | null>(null)
  const [defaultModel, setDefaultModel] = useState<string>(() => getStoredModel())

  function pickDefaultModel(slot: string) {
    setDefaultModel(slot)
    setStoredModel(slot)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="text-[13px] text-muted-foreground mt-1.5">Pause Lucy, pick your default model, bring your own provider keys.</p>
      </div>

      <Card>
        <CardHeader title="Build" subtitle="Git SHA of the code this server is running. Changes after a deploy lands." />
        <CardBody>
          <BuildCard />
        </CardBody>
      </Card>

      <LanguageCard />

      <NotificationsCard />

      <Card>
        <CardHeader title="Default model" subtitle="What Lucy uses when you haven't pinned something else in chat." />
        <CardBody className="space-y-3">
          <select
            value={defaultModel}
            onChange={(e) => pickDefaultModel(e.target.value)}
            className="w-full rounded-xl border border-border bg-black/25 px-3.5 py-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary/40 [&>option]:bg-card"
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.slot} value={m.slot}>{m.label}</option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground">
            {MODEL_OPTIONS.find((m) => m.slot === defaultModel)?.hint}
          </p>
          <p className="text-[10px] text-muted-foreground">
            This default seeds the per-turn picker in <span className="font-medium">Chat</span>.
            Change it there to override for a single message.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Pause" subtitle="Temporarily stop Lucy from running anything on your behalf." />
        <CardBody>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              {pause.data?.paused ? (
                <Badge variant="warn">Paused — Lucy will refuse all calls</Badge>
              ) : (
                <Badge variant="ok">Active</Badge>
              )}
            </div>
            <Button
              variant={pause.data?.paused ? "default" : "outline"}
              size="sm"
              onClick={() => togglePause.mutate(!pause.data?.paused)}
              disabled={togglePause.isPending}
            >
              {pause.data?.paused ? <PlayIcon className="h-3 w-3" /> : <PauseIcon className="h-3 w-3" />}
              {pause.data?.paused ? "Resume" : "Pause"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <AppearanceCard />
      {me?.tier === "admin" && <VoiceCard />}
      {me?.tier === "admin" && <VideoCallCard />}
      {me?.tier === "admin" && <InvitesCard />}

      {/* BYO provider keys: operator-only for now (product decision 2026-10-07).
          The per-user endpoints still exist; flip the condition to re-open it to Pro. */}
      {me?.tier === "admin" && (
        <Card>
          <CardHeader
            title="Your provider keys"
            subtitle="Optional. Lucy already runs on the platform's keys; add your own only if you want those calls billed to your account instead of your Lucy budget."
          />
          <CardBody className="space-y-4">
            {secrets.isLoading && <Spinner />}
            {Array.from(new Set((secrets.data ?? []).map((s) => s.group || "Other"))).map((group) => (
              <div key={group} className="space-y-1.5">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{group}</div>
                <div className="grid gap-2 text-sm">
                  {(secrets.data ?? []).filter((s) => (s.group || "Other") === group).map((s) => (
                    <div key={s.name} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-foreground/[0.04] p-2.5">
                      <div className="min-w-0">
                        <div className="font-medium">{s.label}</div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                          <Badge variant={s.source === "user" ? "ok" : s.source === "unset" ? "muted" : "default"}>
                            {s.source === "user" ? "yours" : s.source === "unset" ? "not available" : "platform"}
                          </Badge>
                          {s.preview && <span className="font-mono truncate">{s.preview}</span>}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setEditing(s)}>
                        <KeyRoundIcon className="h-3 w-3" /> {s.source === "user" ? "Change" : "Add mine"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {me?.tier === "free" && (
        <Card>
          <CardBody>
            <div className="text-sm flex items-center gap-2">
              <EyeOffIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">BYO provider keys are a Pro feature.</span>
            </div>
          </CardBody>
        </Card>
      )}

      <SecretSheet open={!!editing} editing={editing} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["my-secrets"] }) }} />
    </div>
  )
}

interface TTSVoice { voice_id: string; name: string; category?: string | null; labels: Record<string, string>; preview_url?: string | null; description?: string | null }
interface TTSConfig { provider: string; voice_id: string; model: string; models: { id: string; label: string }[] }

const GREEK_SAMPLE = "Γεια σου. Είμαι η Lucy. Πες μου πώς ήταν η μέρα σου και τι θέλεις να κανονίσουμε για αύριο."

/** Lucy's voice: browse the ElevenLabs account's voices, hear them in Greek, pick model + voice. */
function VoiceCard() {
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [voiceId, setVoiceId] = useState<string | null>(null)
  const [model, setModel] = useState<string | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const cfg = useQuery({ queryKey: ["tts-config"], queryFn: () => api<TTSConfig>("/api/dashboard/tts/config") })
  const voices = useQuery({
    queryKey: ["tts-voices", q],
    queryFn: () => api<TTSVoice[]>("/api/dashboard/tts/voices", { query: { q: q || undefined } }),
    staleTime: 5 * 60_000,
  })
  const curVoice = voiceId ?? cfg.data?.voice_id ?? ""
  const curModel = model ?? cfg.data?.model ?? ""
  const save = useMutation({
    mutationFn: async () => {
      await api("/api/dashboard/secrets/ELEVENLABS_VOICE_ID", { method: "PUT", body: { value: curVoice } })
      await api("/api/dashboard/secrets/ELEVENLABS_TTS_MODEL", { method: "PUT", body: { value: curModel } })
    },
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ["tts-config"] }) },
    onError: (e) => setErr((e as { detail?: string }).detail || "Save failed."),
  })

  function stop() { audioRef.current?.pause(); audioRef.current = null; setPlaying(null) }

  async function previewGreek(v: TTSVoice) {
    stop(); setErr(null); setPlaying(v.voice_id)
    try {
      const r = await rawFetch("/api/tts", { method: "POST", body: { text: GREEK_SAMPLE, voice: v.voice_id, format: "mp3" } })
      if (!r.ok) { const j = await r.json().catch(() => null) as { detail?: string } | null; throw new Error(j?.detail || `TTS ${r.status}`) }
      const a = new Audio(URL.createObjectURL(await r.blob()))
      audioRef.current = a
      a.onended = () => setPlaying(null)
      await a.play()
    } catch (e) { setErr((e as Error).message); setPlaying(null) }
  }

  function previewNative(v: TTSVoice) {
    if (!v.preview_url) return
    stop(); setPlaying(v.voice_id + ":native")
    const a = new Audio(v.preview_url)
    audioRef.current = a
    a.onended = () => setPlaying(null)
    void a.play()
  }

  const list = voices.data ?? []
  const current = list.find((v) => v.voice_id === curVoice)
  const dirty = (voiceId !== null && voiceId !== cfg.data?.voice_id) || (model !== null && model !== cfg.data?.model)

  return (
    <Card>
      <CardHeader title="Lucy's voice" subtitle="Admin · which ElevenLabs voice and model speak for Lucy, everywhere (chat, voice mode, Telegram). Try each one in Greek before saving." accent="hsl(200 90% 60%)" />
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Now:</span>
          <Badge variant="ok">{current?.name || cfg.data?.voice_id || "…"}</Badge>
          <span className="text-muted-foreground">·</span>
          <select value={curModel} onChange={(e) => setModel(e.target.value)}
            className="rounded-xl border border-border bg-black/25 px-3 py-1.5 text-sm text-foreground [&>option]:bg-card">
            {(cfg.data?.models ?? []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}Save</Button>
          {save.isSuccess && !dirty && <span className="text-xs text-[hsl(var(--ok))]">Saved — applies immediately.</span>}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter voices: greek, female, warm, young…" />
        {voices.isLoading && <Spinner />}
        {voices.isError && <Toast variant="destructive">{(voices.error as { detail?: string }).detail || "Couldn't load voices (is ELEVENLABS_API_KEY set?)"}</Toast>}
        <div className="max-h-[420px] divide-y divide-border overflow-y-auto rounded-xl border border-border">
          {list.map((v) => (
            <div key={v.voice_id} className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${v.voice_id === curVoice ? "bg-primary/10" : ""}`}>
              <button onClick={() => setVoiceId(v.voice_id)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{v.name}</span>
                  {v.voice_id === curVoice && <Badge variant="ok">selected</Badge>}
                  {v.category && <Badge variant="muted">{v.category}</Badge>}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {Object.values(v.labels).join(" · ")}{v.description ? ` · ${v.description}` : ""}
                </div>
              </button>
              <Button size="sm" variant={playing === v.voice_id ? "default" : "outline"} onClick={() => (playing === v.voice_id ? stop() : previewGreek(v))}>
                {playing === v.voice_id ? "Stop" : "Ελληνικά"}
              </Button>
              {v.preview_url && (
                <Button size="sm" variant="ghost" onClick={() => (playing === v.voice_id + ":native" ? stop() : previewNative(v))}>
                  {playing === v.voice_id + ":native" ? "Stop" : "Sample"}
                </Button>
              )}
            </div>
          ))}
          {!voices.isLoading && list.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground">No voices match.</div>}
        </div>
        <CloneVoice onDone={() => { setVoiceId(null); qc.invalidateQueries({ queryKey: ["tts-config"] }); qc.invalidateQueries({ queryKey: ["tts-voices"] }) }} />
        <p className="text-[11px] text-muted-foreground">
          Want a specific Greek voice that isn't here? Add it to your ElevenLabs library (Voice Library → filter Greek → Add) or clone one; it shows up here on refresh. The "Ελληνικά" button synthesises a Greek sentence with the model selected above, so you hear exactly what Lucy will sound like.
        </p>
        {err && <Toast variant="destructive">{err}</Toast>}
      </CardBody>
    </Card>
  )
}

interface HeygenAvatar { avatar_id: string; avatar_name: string; gender?: string; preview_image_url?: string }
interface HeygenVoice { voice_id: string; name: string; gender?: string; language?: string }
interface VideoCallConfig { avatar_id: string; voice_id: string; avatar_from_settings: boolean; voice_from_settings: boolean; usd_per_minute: number }

/** Video call: which HeyGen avatar and voice appear on camera (kv_settings
 *  heygen.call_avatar_id / heygen.call_voice_id). */
function VideoCallCard() {
  const qc = useQueryClient()
  const [avatarId, setAvatarId] = useState<string | null>(null)
  const [voiceId, setVoiceId] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const cfg = useQuery({ queryKey: ["video-call-config"], queryFn: () => api<VideoCallConfig>("/api/dashboard/video-call/config") })
  const avatars = useQuery({
    queryKey: ["heygen-avatars"],
    queryFn: () => api<{ ok: boolean; avatars: HeygenAvatar[]; error?: string }>("/api/dashboard/heygen/avatars"),
    staleTime: 5 * 60_000,
  })
  const voices = useQuery({
    queryKey: ["heygen-voices"],
    queryFn: () => api<{ ok: boolean; voices: HeygenVoice[]; error?: string }>("/api/dashboard/heygen/voices"),
    staleTime: 5 * 60_000,
  })
  const curAvatar = avatarId ?? cfg.data?.avatar_id ?? ""
  const curVoice = voiceId ?? cfg.data?.voice_id ?? ""
  const dirty = (avatarId !== null && avatarId !== cfg.data?.avatar_id) || (voiceId !== null && voiceId !== cfg.data?.voice_id)
  const save = useMutation({
    mutationFn: () => api<VideoCallConfig>("/api/dashboard/video-call/config", { method: "PUT", body: { avatar_id: curAvatar, voice_id: curVoice } }),
    onSuccess: () => { setErr(null); setAvatarId(null); setVoiceId(null); qc.invalidateQueries({ queryKey: ["video-call-config"] }) },
    onError: (e) => setErr((e as { detail?: string }).detail || "Save failed."),
  })
  const avatarList = avatars.data?.avatars ?? []
  const voiceList = voices.data?.voices ?? []
  const picked = avatarList.find((a) => a.avatar_id === curAvatar)
  const selectCls = "min-w-0 max-w-full rounded-xl border border-border bg-black/25 px-3 py-1.5 text-sm text-foreground [&>option]:bg-card"

  return (
    <Card>
      <CardHeader
        title="Βιντεοκλήση"
        subtitle="Admin · the HeyGen avatar and voice that take video calls. Create the avatar once in HeyGen (Avatars → Photo Avatar) from Lucy's reference photo, and import the ElevenLabs voice into HeyGen (Voices → Add voice → ElevenLabs) so she sounds the same on camera. Both appear here after a refresh."
        accent="hsl(38 90% 60%)"
      />
      <CardBody className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Avatar {cfg.data && !cfg.data.avatar_from_settings && <Badge variant="muted">default</Badge>}</span>
            <select value={curAvatar} onChange={(e) => setAvatarId(e.target.value)} className={selectCls}>
              {!avatarList.some((a) => a.avatar_id === curAvatar) && curAvatar && <option value={curAvatar}>{curAvatar}</option>}
              {avatarList.map((a) => <option key={a.avatar_id} value={a.avatar_id}>{a.avatar_name}{a.gender ? ` · ${a.gender}` : ""}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Voice {cfg.data && !cfg.data.voice_from_settings && <Badge variant="muted">default</Badge>}</span>
            <select value={curVoice} onChange={(e) => setVoiceId(e.target.value)} className={selectCls}>
              {!voiceList.some((v) => v.voice_id === curVoice) && curVoice && <option value={curVoice}>{curVoice}</option>}
              {voiceList.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.language ? ` · ${v.language}` : ""}{v.gender ? ` · ${v.gender}` : ""}</option>)}
            </select>
          </label>
        </div>
        {picked?.preview_image_url && <img src={picked.preview_image_url} alt="" className="h-24 w-24 rounded-xl object-cover ring-1 ring-border" />}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}Save</Button>
          {save.isSuccess && !dirty && <span className="text-xs text-[hsl(var(--ok))]">Saved — the next call uses it.</span>}
          {cfg.data && <span className="text-[11px] text-muted-foreground">≈ ${cfg.data.usd_per_minute.toFixed(2)}/min platform cost (HEYGEN_CALL_USD_PER_MINUTE)</span>}
        </div>
        {(avatars.data?.error || voices.data?.error) && <Toast variant="destructive">{avatars.data?.error || voices.data?.error}</Toast>}
        {(avatars.isLoading || voices.isLoading) && <Spinner />}
        {err && <Toast variant="destructive">{err}</Toast>}
      </CardBody>
    </Card>
  )
}

/** Instant voice clone: 1–25 clean samples (1–3 minutes total) → a new
 *  ElevenLabs voice that becomes Lucy's voice everywhere. */
function CloneVoice({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("Lucy")
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  async function clone() {
    if (!files.length || !name.trim()) return
    setBusy(true); setErr(null); setMsg(null)
    try {
      const fd = new FormData()
      for (const f of files) fd.append("files", f, f.name)
      const r = await rawFetch(`/api/dashboard/tts/clone?name=${encodeURIComponent(name.trim())}`, { method: "POST", rawBody: fd })
      if (!r.ok) {
        const body = await r.json().catch(() => ({})) as { detail?: string }
        throw new Error(body.detail || `Clone failed (${r.status})`)
      }
      const out = await r.json() as { voice_id: string; name: string }
      setMsg(`Cloned "${out.name}" and made it Lucy's voice.`)
      setFiles([])
      onDone()
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  return (
    <div className="space-y-2 rounded-xl bg-secondary/60 p-3 ring-1 ring-inset ring-border">
      <div className="text-[13px] font-medium">Clone a voice</div>
      <p className="text-[11px] text-muted-foreground">Upload 1–25 clean recordings of the voice you want (ideally 1–3 minutes of speech in total, no music). Lucy will speak with it everywhere right away.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Voice name" className="w-40" />
        <input
          type="file"
          accept="audio/*,.m4a,.mp3,.wav,.ogg"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          className="text-xs text-muted-foreground file:mr-2 file:rounded-full file:border-0 file:bg-card file:px-3 file:py-1.5 file:text-xs file:text-foreground file:ring-1 file:ring-inset file:ring-border"
        />
        <Button size="sm" disabled={busy || !files.length || !name.trim()} onClick={clone}>{busy ? <Spinner /> : null}Clone &amp; use</Button>
      </div>
      {files.length > 0 && <div className="text-[11px] text-muted-foreground">{files.length} file{files.length === 1 ? "" : "s"} · {files.map((f) => f.name).join(", ")}</div>}
      {msg && <div className="text-xs text-[hsl(var(--ok))]">{msg}</div>}
      {err && <Toast variant="destructive">{err}</Toast>}
    </div>
  )
}

function AppearanceCard() {
  const t = useT()
  const [theme, setThemeState] = useState<Theme>(getTheme())
  const pick = (th: Theme) => { setTheme(th); setThemeState(th) }
  const opts: [Theme, string][] = [["system", t("theme.system")], ["dark", t("theme.dark")], ["light", t("theme.light")]]
  return (
    <Card>
      <CardHeader title={t("theme.title")} subtitle={t("theme.sub")} />
      <CardBody>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("theme.title")}>
          {opts.map(([th, label]) => (
            <button
              key={th}
              type="button"
              role="radio"
              aria-checked={theme === th}
              onClick={() => pick(th)}
              className={`h-10 rounded-full px-4 text-sm font-medium transition ${theme === th ? "bg-primary text-primary-foreground" : "bg-card text-foreground ring-1 ring-inset ring-border hover:bg-secondary"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">{t("theme.hint")} <kbd className="rounded bg-secondary px-1 font-mono text-[10px] ring-1 ring-inset ring-border">⌘K</kbd></p>
      </CardBody>
    </Card>
  )
}

function NotificationsCard() {
  const t = useT()
  const me = auth.me()
  const prefs = (me as { notify_prefs?: Record<string, boolean> } | null)?.notify_prefs ?? {}
  const [browserOn, setBrowserOn] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!pushSupported()) { setBrowserOn(false); return }
    currentSubscription().then((s) => setBrowserOn(!!s)).catch(() => setBrowserOn(false))
  }, [])
  const save = useMutation({
    mutationFn: (notify_prefs: Record<string, boolean>) => api("/api/me/profile", { method: "PATCH", body: { notify_prefs } }),
    onSuccess: () => auth.refreshMe(),
  })
  async function toggleBrowser() {
    setBusy(true); setErr(null)
    try {
      if (browserOn) { await unsubscribePush(); setBrowserOn(false) }
      else { const ok = await subscribePush(); setBrowserOn(ok); if (!ok) setErr(t("notif.unsupported")) }
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  const Row = ({ k, label }: { k: string; label: string }) => (
    <label className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span>{label}</span>
      <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]"
        checked={prefs[k] !== false}
        onChange={(e) => save.mutate({ [k]: e.target.checked })} />
    </label>
  )
  return (
    <Card>
      <CardHeader title={t("notif.card")} subtitle={t("notif.cardSub")} />
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-foreground/[0.04] px-3 py-2.5">
          <div>
            <div className="text-sm font-medium">{t("notif.browser")}</div>
            <div className="text-xs text-muted-foreground">
              {!pushSupported() ? t("notif.unsupported") : browserOn ? t("notif.browserOn") : t("notif.browserOff")}
            </div>
          </div>
          <Button size="sm" variant={browserOn ? "outline" : "default"} disabled={busy || !pushSupported() || browserOn === null} onClick={toggleBrowser}>
            {busy ? <Spinner /> : null}{browserOn ? t("notif.disable") : t("notif.enable")}
          </Button>
        </div>
        <div className="divide-y divide-border">
          <Row k="telegram" label={t("notif.telegram")} />
          <Row k="whatsapp" label={t("notif.whatsapp")} />
          <Row k="push" label={t("notif.push")} />
          <Row k="web_push" label={t("notif.webpush")} />
          <Row k="email" label={t("notif.email")} />
          <Row k="email_digest" label={t("notif.digest")} />
          <Row k="hourly_operator" label={t("notif.hourly")} />
        </div>
        {err && <Toast variant="destructive">{err}</Toast>}
      </CardBody>
    </Card>
  )
}

function LanguageCard() {
  const t = useT()
  const me = auth.me()
  const [ui, setUi] = useState<Locale>(me?.ui_locale ?? "en")
  const [reply, setReply] = useState<"mirror" | "el" | "en">(me?.reply_language ?? "mirror")
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: (body: { ui_locale: Locale; reply_language: "mirror" | "el" | "en" }) =>
      api("/api/me/profile", { method: "PATCH", body }),
    onSuccess: async (_d, body) => {
      setLocale(body.ui_locale)
      await auth.refreshMe()
      setSaved(true)
      setTimeout(() => setSaved(false), 1500)
    },
  })
  return (
    <Card>
      <CardHeader title={t("lang.title")} subtitle={t("lang.subtitle")} />
      <CardBody className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            <span>{t("lang.ui")}</span>
            <select value={ui} onChange={(e) => setUi(e.target.value as Locale)}
              className="w-full rounded-xl border border-border bg-black/25 px-3.5 py-2.5 text-sm font-normal text-foreground [&>option]:bg-card">
              <option value="el">{t("lang.el")}</option>
              <option value="en">{t("lang.en")}</option>
            </select>
          </label>
          <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
            <span>{t("lang.reply")}</span>
            <select value={reply} onChange={(e) => setReply(e.target.value as "mirror" | "el" | "en")}
              className="w-full rounded-xl border border-border bg-black/25 px-3.5 py-2.5 text-sm font-normal text-foreground [&>option]:bg-card">
              <option value="mirror">{t("lang.mirror")}</option>
              <option value="el">{t("lang.el")}</option>
              <option value="en">{t("lang.en")}</option>
            </select>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ ui_locale: ui, reply_language: reply })}>
            {save.isPending ? <Spinner /> : null}{t("account.save")}
          </Button>
          {saved && <span className="text-xs text-[hsl(var(--ok))]">{t("lang.saved")}</span>}
          {save.isError && <Toast variant="destructive">{(save.error as { detail?: string }).detail || "Failed."}</Toast>}
        </div>
      </CardBody>
    </Card>
  )
}

interface InviteOut {
  id: string
  code: string
  email: string | null
  note: string | null
  max_uses: number
  uses: number
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
  created_at: string
  signup_url: string
}

function inviteStatus(i: InviteOut): { label: string; variant: "ok" | "muted" | "warn" } {
  if (i.revoked_at) return { label: "Revoked", variant: "muted" }
  if (i.expires_at && new Date(i.expires_at) < new Date()) return { label: "Expired", variant: "muted" }
  if (i.uses >= i.max_uses) return { label: "Used", variant: "muted" }
  return { label: `${i.max_uses - i.uses} left`, variant: "ok" }
}

function InvitesCard() {
  const qc = useQueryClient()
  const [email, setEmail] = useState("")
  const [note, setNote] = useState("")
  const [maxUses, setMaxUses] = useState(1)
  const [days, setDays] = useState(14)
  const [err, setErr] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const invites = useQuery({
    queryKey: ["admin-invites"],
    queryFn: () => api<InviteOut[]>("/api/admin/invites"),
  })

  const create = useMutation({
    mutationFn: () =>
      api<InviteOut>("/api/admin/invites", {
        method: "POST",
        body: {
          email: email.trim() || null,
          note: note.trim() || null,
          max_uses: maxUses,
          expires_in_days: days,
        },
      }),
    onSuccess: () => {
      setErr(null)
      setEmail("")
      setNote("")
      qc.invalidateQueries({ queryKey: ["admin-invites"] })
    },
    onError: (e) => setErr((e as { detail?: string }).detail || "Could not create the invite."),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/admin/invites/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-invites"] }),
  })

  async function copy(i: InviteOut) {
    try {
      await navigator.clipboard.writeText(i.signup_url)
      setCopied(i.id)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setErr("Clipboard unavailable — copy the link manually.")
    }
  }

  return (
    <Card>
      <CardHeader
        title="Invitations"
        subtitle="Admin-only · registration is invite-only. Issue a code, send the link, revoke if needed."
        accent="hsl(160 84% 39%)"
      />
      <CardBody className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            type="email"
            placeholder="Bind to email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />
          <Input
            type="text"
            placeholder="Note (who is this for?)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoComplete="off"
          />
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Uses
            <Input
              type="number"
              min={1}
              max={1000}
              value={maxUses}
              onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
              className="h-9 w-24"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Expires in (days)
            <Input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
              className="h-9 w-24"
            />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? <Spinner /> : null}
            Create invite
          </Button>
          {err && <Toast variant="destructive">{err}</Toast>}
        </div>

        {invites.isLoading ? (
          <Spinner />
        ) : (invites.data?.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground">No invitations yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {invites.data!.map((i) => {
              const st = inviteStatus(i)
              return (
                <li key={i.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <code className="font-mono text-xs">{i.code}</code>
                  <Badge variant={st.variant}>{st.label}</Badge>
                  <span className="text-xs text-muted-foreground truncate">
                    {i.email || "any email"}
                    {i.note ? ` · ${i.note}` : ""}
                    {i.expires_at ? ` · until ${new Date(i.expires_at).toLocaleDateString()}` : ""}
                  </span>
                  <span className="ml-auto flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => copy(i)}>
                      {copied === i.id ? "Copied" : "Copy link"}
                    </Button>
                    {!i.revoked_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(i.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}

function SecretSheet({
  open, onClose, editing,
}: { open: boolean; onClose: () => void; editing: MySecretEntry | null }) {
  const [value, setValue] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  async function save() {
    if (!editing) return
    setBusy(true); setErr(null)
    try {
      await api(`/api/me/secrets/${encodeURIComponent(editing.name)}`, { method: "PUT", body: { value } })
      setValue("")
      onClose()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  async function clear() {
    if (!editing) return
    setBusy(true); setErr(null)
    try {
      await api(`/api/me/secrets/${encodeURIComponent(editing.name)}`, { method: "DELETE" })
      onClose()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={editing ? `Set ${editing.label}` : ""}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {editing?.source === "user" && <Button variant="destructive" onClick={clear} disabled={busy}>Clear</Button>}
          <Button onClick={save} disabled={busy || !value.trim()}>{busy ? <Spinner /> : null}Save</Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {editing?.help_text && <p className="text-xs text-muted-foreground">{editing.help_text}</p>}
        <Input type="password" placeholder="Paste the secret" value={value} onChange={(e) => setValue(e.target.value)} />
        {editing?.help_url && (
          <a href={editing.help_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground underline">
            Get a key →
          </a>
        )}
        {err && <Toast variant="destructive">{err}</Toast>}
      </div>
    </Sheet>
  )
}
